<script setup>
import { watch, onBeforeUnmount, ref } from 'vue';
import { RpImageEditor } from '@rageshpikalmunde/rp-image-editor';

const props = defineProps({
  imageUrl: {
    type: String,
    required: false,
    default: undefined,
  },
  isOpen: Boolean,
});
const emit = defineEmits(['save', 'close']);

const editorContainer = ref(null);
let editorInstance = null;
let isClosing = false;

const cleanup = () => {
  editorInstance?.destroy();
  editorInstance = null;
  isClosing = false;
};

watch(
  () => props.isOpen,
  async open => {
    if (!open || !props.imageUrl) return;

    await new Promise(resolve => setTimeout(resolve, 0));

    isClosing = false;

    const handleApply = async () => {
      if (!editorInstance || isClosing) return;
      try {
        const result = await editorInstance.getResult();
        cleanup();
        if (result?.file) {
          emit('save', result.file);
        } else {
          emit('close');
        }
      } catch (err) {
        console.error('[RpImageEditor] Export failed:', err);
      }
    };

    const handleClose = () => {
      if (isClosing) return;
      isClosing = true;
      cleanup();
      emit('close');
    };

    editorInstance = new RpImageEditor(editorContainer.value, {
      exportFormat: 'jpeg',
      exportQuality: 0.85,
      showToolbar: true,
      onApply: handleApply,
      onClose: handleClose,
    });

    try {
      await editorInstance.loadImage(props.imageUrl);
      // когда изображение загружено, ставим нужный инструмент
      editorInstance.setMode('shape-arrow');
    } catch (err) {
      console.error('[RpImageEditor] Failed to load image:', err);
      cleanup();
      emit('close');
    }
  }
);

onBeforeUnmount(() => {
  cleanup();
});
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen" class="editor-modal-overlay">
      <div ref="editorContainer" class="editor-modal-container" />
    </div>
  </Teleport>
</template>

<style scoped>
.editor-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.editor-modal-container {
  width: 100%;
  height: 100%;
  max-width: 1400px;
  max-height: 90vh;
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}
</style>
